// InputMessageContent.tsx
import { forwardRef, useImperativeHandle, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp } from 'react-bootstrap-icons';
import { useOutsideAlerter } from '../hooks/useOutsideAlerter';
import { useSelector } from 'react-redux';
import { getAllUserDetails, getDefaultShortUrl } from '../pages/UserPages/accounts/Overview/OverviewSlice/UserDetailsSlice';
import { ENV } from '../Utils/_helpers/EnvHelper';
import { InputMessageContentProps, InputMessageContentRef, OptOutItem, UserMessage } from './types/InputMessageContent';
import { useContentEditable } from './hooks/useContentEditable';
import { DOMUtils } from '../Utils/domUtils';
import clsx from 'clsx';

export const InputMessageContent = forwardRef<InputMessageContentRef, InputMessageContentProps>(
  (
    {
      isMessageSecond = false,
      disableTextfield,
      maxLength,
      customOptOutList,
      userMessage,
      onChange,
      onChangeOptOut,
      value,
      showSelect,
      isMobile,
    },
    ref
  ) => {
    const [isOpenDropdown, setIsOpenDropdown] = useState(false);
    const dropdownRef = useRef(null);
    const userValues = useSelector(getAllUserDetails);
    const defaultDomain = useSelector(getDefaultShortUrl)
    
    // Get domain options from custom hook
    const { domainOptions, addedCustomOptOuts } = useOptOutConfig({
      customOptOutList,
      userMessage,
      defaultDomain,
      userValues,
    });

    // ContentEditable hook
    const {
      handleInput,
      handleKeyDown,
      insertPlaceholder,
      contentEditableRef,
      getCaretPosition,
      updateContentEditable,
      getRawText,
      handlePaste,
    } = useContentEditable({
      domainOptions,
      maxLength,
      onChange: (newValue) => onChange({
        target: {
          name: isMessageSecond ? 'messageSecond' : 'message',
          value: newValue
        }
      }),
      initialValue: value,
    });

    // Selection state management
    const [selection, setSelection] = useState<{ start: number; end: number }>({
      start: 0,
      end: 0,
    });

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      insertPlaceholder,
      focus: () => {
        // Force content refresh before focusing
        updateContentEditable(value);
        // DOMUtils.focusElement(contentEditableRef.current);
      },
      getRawText,
      selectionStart: selection.start,
      selectionEnd: selection.end,
    }));

    // Update the value tracking useEffect
    useEffect(() => {
      // Always update when component mounts or value changes
      updateContentEditable(value);
      
      // Optional: Add cleanup if needed when component unmounts
      return () => {
        DOMUtils.cleanEditableContent(contentEditableRef.current);
      };
    }, [value, updateContentEditable]);

    // Selection tracking
    useEffect(() => {
      const updateSelection = () => {
        const pos = getCaretPosition();
        setSelection({ start: pos, end: pos });
      };

      const currentRef = contentEditableRef.current;
      currentRef?.addEventListener('keyup', updateSelection);
      currentRef?.addEventListener('mouseup', updateSelection);

      return () => {
        currentRef?.removeEventListener('keyup', updateSelection);
        currentRef?.removeEventListener('mouseup', updateSelection);
      };
    }, [contentEditableRef, getCaretPosition]);

    // Badge deletion handler
    useEffect(() => {
      return DOMUtils.registerBadgeDeleteHandler(
        contentEditableRef.current,
        handleInput
      );
    }, [contentEditableRef, handleInput]);

    // Dropdown visibility
    useOutsideAlerter(dropdownRef, () => setIsOpenDropdown(false));

    const getReplyOptOutLabel = () => {
      const useStopReply = isMessageSecond 
        ? userMessage?.replyStopToOptOutSecond
        : userMessage?.replyStopToOptOut;

      let optoutLabel = ""
      let optoutLabelSecond = ""
      if (userMessage?.optout && userMessage?.optoutId && userMessage?.optoutId.length > 0) {
        const optOutData = addedCustomOptOuts.find((key: any) => key.value === userMessage?.optoutId)
        if (optOutData) {
          optoutLabel = optOutData.label
        }
      } 

      if (userMessage?.optoutSecond && userMessage?.optoutIdSecond && userMessage?.optoutIdSecond.length > 0) {
        const optOutData = addedCustomOptOuts.find((key: any) => key.value === userMessage?.optoutIdSecond)
        if (optOutData) {
          optoutLabelSecond = optOutData.label
        }
      }

      const useStopLink = isMessageSecond ? optoutLabelSecond : optoutLabel

      // This is to prevent showing Reply 'STOP' to opt-out for business sender type
      if (userMessage?.senderType?.includes('business')) {
        if(useStopLink && useStopLink.length > 0) {
            return `STOP:{stoptxt.au/${useStopLink}}`;
        }else{
            return `STOP:{stoptxt.au/${addedCustomOptOuts[0]?.label}}`;
        }
      }
      
      return useStopReply
        ? "Reply 'STOP' to opt-out"
        : `STOP:{stoptxt.au/${useStopLink}}`;
    };

    return (
      <div className={clsx("form-control form-control-solid border-bottom border-top rounded-0",
          {
            "border-left-2 border-right-2": isMobile,
            "border-left-0 border-right-0": !isMobile,
          }
          )}
        style={{ minHeight: '150px', backgroundColor: isMobile ? "#f6f8fa" : "" }}>
        <div
          ref={contentEditableRef}
          contentEditable={!disableTextfield}
          onInput={handleInput}
          onKeyDown={(e) => {
            handleKeyDown(e)
          }}
          data-kt-element="input"
          data-testid="textareaMessage"
          data-placeholder="Enter your message here..."
          className="contenteditable-with-placeholder"
          onPaste={handlePaste}
        />

        {customOptOutList.length > 0 && showSelect?.includes('opt-out') && (
          <OptOutDropdown
            ref={dropdownRef}
            isOpen={isOpenDropdown}
            options={addedCustomOptOuts}
            selectedLabel={getReplyOptOutLabel()}
            onToggle={() => setIsOpenDropdown(!isOpenDropdown)}
            onChange={onChangeOptOut}
          />
        )}
      </div>
    );
  }
);

// Helper hooks and components
const useOptOutConfig = ({
  customOptOutList,
  userMessage,
  defaultDomain,
  userValues
}: {
  customOptOutList: OptOutItem[];
  userMessage?: UserMessage;
  defaultDomain: string;
  userValues: any;
}) => {
  const [domainOptions, setDomainOptions] = useState([defaultDomain]);

  useEffect(() => {
    if (userValues?.customShortLink?.status) {
      setDomainOptions([defaultDomain, userValues.customShortLink?.domain]);
    }
  }, [userValues, defaultDomain]);

  const addedCustomOptOuts = [
    { value: 'ReplyStop', label: "Reply 'STOP' to opt-out" },
    ...customOptOutList,
  ].filter(it => 
    userMessage?.senderType?.includes('business') ? it.value !== 'ReplyStop' : true
  );

  return { domainOptions, addedCustomOptOuts };
};

const OptOutDropdown = forwardRef<HTMLDivElement, {
  isOpen: boolean;
  options: OptOutItem[];
  selectedLabel: string;
  onToggle: () => void;
  onChange: (optOut: OptOutItem) => void;
}>(({ isOpen, options, selectedLabel, onToggle, onChange }, ref) => (
  <div className="dropdown" ref={ref}>
    <button
      className="btn dropdown-toggle py-2"
      type="button"
      id='dropdownMenuButton'
      data-bs-toggle='dropdown'
      aria-expanded={isOpen ? 'true' : 'false'}
      onClick={onToggle}
      style={{
        marginTop: '6px',
        borderRadius: '6px',
        padding: '4px 8px',
        backgroundColor: '#E8E0FE',
        fontWeight: 400,
        fontSize: '12px',
        lineHeight: '16.8px',
        color: '#333333',
      }}
    >
      {selectedLabel} {isOpen ? <ChevronUp className="ms-5" /> : <ChevronDown className="ms-5" />}
    </button>
    
    <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton">
      {options.map(optOut => (
        <li key={optOut.value}>
          <div className="dropdown-item" onClick={() => onChange(optOut)}>
            {optOut.label}
          </div>
        </li>
      ))}
    </ul>
  </div>
));